/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { UsePortFormReturn } from "../hooks/usePortForm";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import { GenericTag } from "@ui-components/Tag/GenericTag";

export interface PortCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    dataCenterOptions: string[];
    hardwareOptions: string[];
    modalDcName: string;
    modalHwId: number | null;
    getHardwareNameById: (hwId: number | null) => string;
    handleDataCenterSelect: (dcName: string, isModal?: boolean) => void;
    handleHardwareSelect: (hwName: string, isModal?: boolean) => void;
    // Port dropdown
    portOptions: string[];
    selectedPortId: number | null;
    getPortLabelById: (portId: number | null) => string;
    handlePortSelect: (portLabel: string) => void;
    // Form state management
    portForm: UsePortFormReturn;
    // Tenant dropdown
    tenantOptions: string[];
    selectedTenantId: number | null;
    getTenantLabelById: (tenantId: number | null) => string;
    handleTenantSelect: (tenantLabel: string) => void;
  }
  
  export const PortCreateModal: React.FC<PortCreateModalProps> = ({
    isOpen,
    onClose,
    onSave,
    dataCenterOptions,
    hardwareOptions,
    modalDcName,
    modalHwId,
    getHardwareNameById,
    handleDataCenterSelect,
    handleHardwareSelect,
    portOptions,
    selectedPortId,
    getPortLabelById,
    handlePortSelect,
    portForm,
    tenantOptions,
    selectedTenantId,
    getTenantLabelById,
    handleTenantSelect,
  }) => {
    const { formData, updateField } = portForm;
  
    const handleStatusToggle = (isActive: boolean) => {
        // Switch: false = Draft (0), true = Active (1)
        updateField("isDraft", isActive ? 1 : 0);
      };
    const isActive = formData.isDraft === 1;

    // Port type switch handler
    const handlePortTypeChange = (isVirtual: boolean) => {
      updateField("isVirtualPort", isVirtual);
    };
        
    const isVirtual = formData.isVirtualPort === true;
    
    // Provider dropdown options
    const providerOptions = ["Console connect", "Megaport", "Equinix"];
    const providerAccountOptions = ["Telecom", "CTX"];

    // Crossconnect dropdown options
    const crossconnectTypeOptions = ["SMF", "UTP", "GLC-T"];
    const crossconnectSpeedOptions = ["100Mb", "1G", "100G"];
    
    // Helper to get provider account label from value
    const getProviderAccountLabel = (value: string): string => {
      if (value === "1") return "Telecom";
      if (value === "2") return "CTX";
      return "";
    };
    return (
      <GenericModal
        isOpen={isOpen}
        onClose={onClose}
        title="Add Port"
        customWidth="max-w-5xl"
      >
        <div className="space-y-6">
        {/* Tenant and Port Type Selection - Above Location Section */}
          <GenericCard isCollapsible={false} className="p-5">
            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col col-span-2 space-y-2">
                <label className="text-sm font-medium">Tenant</label>
                <GenericDropdown
                  items={tenantOptions}
                  value={getTenantLabelById(selectedTenantId)}
                  onSelect={handleTenantSelect}
                  defaultLabel="Select Tenant"
                  maxWidthClass="w-full"
                  dropdownMaxHeightClass="max-h-48"
                  fixedPosition={true}
                />
              </div>
              <div className="flex flex-col space-y-2 items-end">
                <label className="text-sm font-medium text-left">Port type</label>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${!isVirtual ? "font-semibold text-semantic-text-primary" : "text-semantic-text-secondary"}`}>
                    Physical
                  </span>
                  <GenericSwitch
                    checked={isVirtual}
                    onChange={handlePortTypeChange}
                  />
                  <span className={`text-sm ${isVirtual ? "font-semibold text-semantic-text-primary" : "text-semantic-text-secondary"}`}>
                    Virtual
                  </span>
                </div>
              </div>
            </div>
          </GenericCard>
          {/* Data Center, Hardware, and Port Selection - Always visible (only for Physical) */}
          {!isVirtual && (
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Location Selection
              </h3>
              <div className="flex flex-col space-y-4">
                {/* First row: Data Center and Hardware in 2 columns */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col space-y-2">
                    <label className="text-sm font-medium">Data Center</label>
                    <GenericDropdown
                      items={dataCenterOptions}
                      value={modalDcName}
                      onSelect={(dcName) => handleDataCenterSelect(dcName, true)}
                      defaultLabel="Select DC"
                      maxWidthClass="w-full"
                      dropdownMaxHeightClass="max-h-48"
                      fixedPosition={true}
                    />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <label className="text-sm font-medium">Hardware</label>
                    <GenericDropdown
                      items={hardwareOptions}
                      value={getHardwareNameById(modalHwId)}
                      onSelect={(hwName) => handleHardwareSelect(hwName, true)}
                      disabled={!modalDcName}
                      defaultLabel="Select Hardware"
                      maxWidthClass="w-full"
                      dropdownMaxHeightClass="max-h-48"
                      fixedPosition={true}
                    />
                  </div>
                </div>
                {/* Second row: Port Searchable Dropdown - Full width */}
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Port</label>
                  <GenericSearchableDropdown
                    items={portOptions}
                    value={getPortLabelById(selectedPortId)}
                    onSelect={handlePortSelect}
                    disabled={!modalHwId}
                    defaultLabel="Select Port"
                    emptyItemsMessage="No ports available"
                    widthClass="w-full"
                    maxWidthClass=""
                    inputMaxWidth="100%"
                  />
                </div>
              </div>
            </GenericCard>
          )}
  
          {/* Conditional rendering based on port type */}
          {isVirtual && (
            /* Virtual Port UI - Provider Data Section */
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Provider Data
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Provider</label>
                  <GenericDropdown
                    items={providerOptions}
                    value={formData.editProvider || ""}
                    onSelect={(value) => updateField("editProvider", value)}
                    defaultLabel="Select Provider"
                    maxWidthClass="w-full"
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Provider Account</label>
                  <GenericDropdown
                    items={providerAccountOptions}
                    value={getProviderAccountLabel(formData.editProviderAccount || "")}
                    onSelect={(value) => {
                      // Map "Telecom" to "1", "CTX" to "2"
                      const accountValue = value === "Telecom" ? "1" : value === "CTX" ? "2" : "";
                      updateField("editProviderAccount", accountValue);
                    }}
                    defaultLabel="Select Provider Account"
                    maxWidthClass="w-full"
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Provider Id</label>
                  <GenericInput
                    label=""
                    value={formData.editProviderId || ""}
                    onChange={(value) => updateField("editProviderId", value)}
                    placeholder="Enter Provider Id"
                  />
                </div>
              </div>
            </GenericCard>
          )}

          {/* Port Information Section - Common for both Physical and Virtual */}
          <GenericCard isCollapsible={false} className="p-5">
            <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
              Port Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <GenericInput
                label="Subscription ID"
                value={formData.subscriptionId}
                onChange={(value) => updateField("subscriptionId", value)}
              />
              <GenericInput 
                label="Port Name" 
                value={formData.portName} 
                onChange={(value) => updateField("portName", value)} 
              />
              <GenericInput 
                label="Tags" 
                value={formData.tags?.join(", ") || ""} 
                onChange={(value) => {
                  // Convert comma-separated string to array, trim each tag, and filter empty strings
                  const tagsArray = value
                    .split(",")
                    .map(tag => tag.trim())
                    .filter(tag => tag.length > 0);
                  updateField("tags", tagsArray);
                }} 
                placeholder="Enter tags separated by commas"
              />
              <GenericInput
                label="Capacity (Mbps)"
                value={formData.editCapacityMbps || formData.portType}
                onChange={(value) => updateField("editCapacityMbps", value)}
              />
            </div>
            {/* Status Toggle Switch */}
            <div className="flex items-center justify-end gap-2 mt-4">
              <label className="text-sm font-medium text-semantic-text-secondary flex items-center gap-1">
                Status:
                <GenericTooltip
                  text="You can toggle the status of this configuration by using the switch."
                  position="top"
                />
              </label>
              <span className={`text-sm ${!isActive ? "font-semibold text-semantic-text-primary" : "text-semantic-text-secondary"}`}>
                Draft
              </span>
              <GenericSwitch 
                checked={isActive}
                onChange={handleStatusToggle}
              />
              <span className={`text-sm ${isActive ? "font-semibold text-semantic-text-primary" : "text-semantic-text-secondary"}`}>
                Active
              </span>
            </div>
          </GenericCard>

            {/* Crossconnect Information Section - Only for Virtual Ports */}
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Crossconnect Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Type</label>
                  <GenericDropdown
                    items={crossconnectTypeOptions}
                    value={formData.crossconnectType || ""}
                    onSelect={(value) => updateField("crossconnectType", value)}
                    defaultLabel="Select Type"
                    maxWidthClass="w-full"
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Speed</label>
                  <GenericDropdown
                    items={crossconnectSpeedOptions}
                    value={formData.crossconnectSpeed || ""}
                    onSelect={(value) => updateField("crossconnectSpeed", value)}
                    defaultLabel="Select Speed"
                    maxWidthClass="w-full"
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium">Id</label>
                  <GenericInput
                    label=""
                    value={formData.crossconnectId || ""}
                    onChange={(value) => updateField("crossconnectId", value)}
                    placeholder="Enter Crossconnect Id"
                  />
                </div>
              </div>
            </GenericCard>
  
        </div>
  
        <div className="flex justify-end gap-2 mt-6">
          <GenericButton 
            label="Cancel" 
            variant="secondary" 
            onClick={onClose} 
          />
          <GenericButton 
            label="Create" 
            variant="brand" 
            onClick={onSave}
          />
        </div>
      </GenericModal>
    );
  };
  
  